/**
 * Smart Variant / VDA v2 attribute metadata for order-service line items
 * (Cancellation, Returns StageZero). Mirrors the fe-common Order Details helper
 * so both surfaces render the same `Label: Value` rows in the show bucket.
 *
 * Under the show bucket of the ramp_svd-rollout-web experiment (useIsSvdEnabled):
 *   - filter `attributes` where `isVda === true`
 *   - sort ascending by `vdaConfig.order` (entries without an order go last)
 *   - map each to `attributeValues[key].displayValue.localisedValue`
 *   - drop entries with empty value or empty label
 *
 * Customization / Personalization / MTO are NOT part of `attributes` per the
 * BE contract — they arrive via `customAttributes` on the line item and render
 * as their own rows unchanged by the gate.
 */

/**
 * BE may send an attribute `title` as a plain string (legacy) or as a
 * LocalisedValue object `{ value, localisedValue }` (Smart Variant / VDA v2
 * contract). Read it via `readAttributeTitle`, never `.trim()` directly.
 */
export interface LocalisedTitle {
  value?: string
  localisedValue?: string
  locale?: string
}

export interface VariantAttributeVdaConfig {
  order?: number | null
  searchTileable?: boolean | null
}

export interface VariantAttribute {
  key: string
  title?: string | LocalisedTitle | null
  isVda: boolean
  /** ECOMP-9742: VDA display order ships as vdaConfig.order. */
  vdaConfig?: VariantAttributeVdaConfig | null
  type: string
  searchTileable?: boolean
}

export interface VariantAttributeValueDisplay {
  value: string
  localisedValue: string
}

export interface VariantAttributeValue {
  type: string
  displayValue: VariantAttributeValueDisplay
  sortOrder?: number | null
  swatchType?: string | null
  hexCode?: string | null
  swatchImageUrl?: string | null
  imageUrl?: string | null
}

export interface VariantAttributesInfo {
  attributes?: VariantAttribute[]
  attributeValues?: Record<string, VariantAttributeValue>
}

export interface VariantAttributeEntry {
  key: string
  label: string
  value: string
}

export interface VariantAttributesCarrier extends VariantAttributesInfo {
  variantDetail?: VariantAttributesInfo | null
}

/**
 * Reads the VDA slice whichever shape the endpoint used: the Order Aggregator nests it under
 * `variantDetail`, the Subscription Service sends it flat on the item. Nested wins when both are
 * present — that is the documented shape.
 */
export function readVariantAttributesInfo(item: VariantAttributesCarrier | null | undefined): VariantAttributesInfo {
  return {
    attributes: item?.variantDetail?.attributes ?? item?.attributes,
    attributeValues: item?.variantDetail?.attributeValues ?? item?.attributeValues,
  }
}

/**
 * camelCase / snake_case / kebab-case → "Title Case" fallback for the display
 * label when the backend has not provided `title` on the attribute.
 */
export function titleFromKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Reads the display label off an attribute `title`, tolerating both the legacy
 * plain-string shape and the VDA v2 `LocalisedValue` object. Prefers
 * `localisedValue`, falls back to `value`, then '' so the caller can fall
 * through to `titleFromKey`.
 */
export function readAttributeTitle(title: VariantAttribute['title']): string {
  if (!title) return ''
  if (typeof title === 'string') return title.trim()
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty '' must fall through to value, not only null/undefined
  return (title.localisedValue || title.value || '').trim()
}

export const IGNORED_VARIANT_VALUES: string[] = [
  'One Size',
  'default',
  'One Color',
  'No Color',

  // fr-ca
  'Taille unique',
  'par défaut',
  'Une couleur',
  'Aucune couleur',

  // en-ca
  'One Colour',
  'No Colour',
]

export function isIgnoredVariantValue(value: string): boolean {
  const normalized = value.trim().toLowerCase()
  return IGNORED_VARIANT_VALUES.some(ignored => ignored.toLowerCase() === normalized)
}

/**
 * Axis record keyed by attribute key, for MATCHING rather than display — product links and the
 * buy-now popup's `selectedOptions`. Reads the canonical `displayValue.value`, NOT
 * `localisedValue`: these values are matched against the product's option values and slugified
 * into `?color=` / `?size=`, so they have to stay locale-stable. `getVariantAttributeEntries`
 * above is the display counterpart and deliberately returns the localised value instead.
 *
 * Ignored placeholders ("One Size", "No Color") are dropped here too — a link carrying
 * `?size=one-size` matches nothing, same as omitting it.
 */
export function getSelectedAttributesRecord(
  info: VariantAttributesInfo | null | undefined,
): Record<string, string> | undefined {
  const attributeValues = info?.attributeValues ?? {}
  const record: Record<string, string> = {}
  ;(info?.attributes ?? [])
    .filter(attr => attr?.isVda)
    .forEach(attr => {
      const value = (attributeValues[attr.key]?.displayValue?.value || '').trim()
      if (value && !isIgnoredVariantValue(value)) record[attr.key] = value
    })
  // undefined, NOT an empty object: callers branch on truthiness to decide between the VDA axes
  // and their legacy fallback, and `{}` is truthy. A non-migrated product in a flag-ON session
  // would otherwise resolve both axes to '' and silently drop them from the link.
  return Object.keys(record).length > 0 ? record : undefined
}

/**
 * Whether the BE has migrated this item, i.e. it carries any VDA axis. A placeholder-only item
 * ("No Color / One Size") is migrated but yields an empty record, so `getSelectedAttributesRecord`
 * alone cannot tell it apart from a non-migrated one.
 */
export function hasVdaAxes(info: VariantAttributesInfo | null | undefined): boolean {
  return (info?.attributes ?? []).some(attr => attr?.isVda)
}

export function getVariantAttributeEntries(info: VariantAttributesInfo | null | undefined): VariantAttributeEntry[] {
  const attributes = info?.attributes
  const attributeValues = info?.attributeValues ?? {}
  if (!attributes?.length) return []

  return attributes
    .filter(attr => attr?.isVda)
    .slice()
    .sort((a, b) => {
      const posA = a.vdaConfig?.order ?? Number.POSITIVE_INFINITY
      const posB = b.vdaConfig?.order ?? Number.POSITIVE_INFINITY
      return posA - posB
    })
    .map(attr => {
      const value = (attributeValues[attr.key]?.displayValue?.localisedValue || '').trim()
      const label = readAttributeTitle(attr.title) || titleFromKey(attr.key)
      return { key: attr.key, label, value }
    })
    .filter(entry => entry.value && entry.label && !isIgnoredVariantValue(entry.value))
}

/**
 * True when the rendered entries already state the given axis — matched on the
 * attribute KEY or on the display LABEL the caller is about to render.
 *
 * The key alone is not enough: a migrated product can carry the measurement under a
 * key that is not literally `inseam`/`length` (e.g. `pantLength`) while its title
 * renders as "Length". Key-only matching therefore let callers append a second
 * "Length: 34" next to the one the VDA rows already showed.
 */
export function entriesStateAxis(entries: VariantAttributeEntry[], axisKey: string, axisLabel?: string): boolean {
  const key = axisKey.trim().toLowerCase()
  const label = axisLabel?.trim().toLowerCase()

  return entries.some(
    entry =>
      String(entry.key).trim().toLowerCase() === key || (!!label && String(entry.label).trim().toLowerCase() === label),
  )
}
